import React, {memo, useCallback, useState} from "react";
import Autocomplete from "@mui/material/Autocomplete";
import {FileSelectOption} from "./FileSelectOption";
import {createPortal} from "react-dom";
import {RemoveFileDialog} from "./RemoveFileDialog";
import {CollapseGroup} from "./CollapseGroup";
import {AutocompleteInput} from "./AutocompleteInput";
import {NodeFile} from "../../models/NodeFile";
import {LessonFlat} from "models/LessonFlat";
import {AutocompleteChangeReason} from "@mui/material";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {UploadFileDialog} from "./UploadFileDialog";
import CustomPopper from "./CustomPopper";
import {useGetNodeFileGroups} from "hooks/useGetNodeVideoGroups";
import {useGetNodeFileList} from "hooks/useGetNodeFileList";
import {useDeleteNodeFile} from "hooks/useDeleteNodeFile";
import {useGetLessonsByNodeFile} from "hooks/useGetLessonsByNodeFile";
import {FileFlat} from "models/FileFlat";
import {useAuthContext} from 'hooks/useAuthContext';
import {UserRoles} from 'constants/userRoles';
import { ContainerBox, AutocompleteContainer, UploadButton } from './NodeFileSearch.styles';
import { ResourceType } from "models/ResourceType";

const listboxProps = { style: { scrollbarWidth: "thin" as "thin" } };

interface NodeFileSearchProps {
    editor: any;
    labelText: string;
    selectedOption: NodeFile;
    sortedOptions: NodeFile[];
    resourceType: ResourceType;
    handleChange: (event: React.SyntheticEvent, value: NodeFile | null, reason: AutocompleteChangeReason) => void;
    handleFocus: () => void;
    setSelectedOption: (nodeFileId: string) => void;
}

const NodeFileSearch = (props: NodeFileSearchProps) => {
    const {editor, labelText, selectedOption, sortedOptions, resourceType, handleChange, handleFocus, setSelectedOption} = props;
    const {fetchNodeFileList} = useGetNodeFileList();
    const {fetchNodeFileGroups} = useGetNodeFileGroups();
    const {removeNodeFile} = useDeleteNodeFile();
    const {fetchLessonsByNodeFile} = useGetLessonsByNodeFile();
    const [optionToRemove, setOptionToRemove] = useState<NodeFile | null>(null);
    const [usedBy, setUsedBy] = useState<LessonFlat[]>([]);
    const [showRemoveDialog, setShowRemoveDialog] = useState<boolean>(false);
    const [showUploadPopup, setShowUploadPopup] = useState<boolean>(false);
    const {userState} = useAuthContext();
    const isAdmin = userState.user.selectedWorkspace.userRole === UserRoles.ADMIN;
    const groups = editor.storage.getGroups(resourceType);

    const handleOptionRemove = useCallback(async (option: NodeFile) => {
        let fileId = option.value;
        let response = null;
        switch (resourceType) {
            case ResourceType.NodeImage:
            case ResourceType.NodeVideo:
            case ResourceType.NodeAudio:
            case ResourceType.NodeInfoIcon:
                response = await fetchLessonsByNodeFile(fileId);
                break;
            default:
                console.error("Unhandled node file type!");
                return;
        }
        
        if (!response?.success) {
            return;
        }

        const lessons = response.data;
        setOptionToRemove(option);
        setUsedBy(lessons);
        setShowRemoveDialog(true);
    }, [fetchLessonsByNodeFile, resourceType]);

    const handleDialogDelete = useCallback(async () => {
        if (!optionToRemove) return;
        let fileId = optionToRemove.value;

        await removeNodeFile(editor, resourceType, fileId);
        await fetchNodeFileList(editor, resourceType);
        await fetchNodeFileGroups(editor, resourceType);
        
        setShowRemoveDialog(false);
        setOptionToRemove(null);

    }, [optionToRemove, removeNodeFile, fetchNodeFileList, editor, resourceType, fetchNodeFileGroups]);

    const handleDialogClose = useCallback(() => {
        setShowRemoveDialog(false);
    }, []);

    const handleUploadClick = useCallback(() => {
        setShowUploadPopup(true);
    }, []);

    const handleUploadPopupClose = useCallback(() => {
        setShowUploadPopup(false);
    }, []);

    const handleFilesUploaded = useCallback(async (uploadedFiles: FileFlat[], allSucceed: boolean) => {
        await fetchNodeFileList(editor, resourceType);

        if (allSucceed && uploadedFiles.length === 1) {
            setSelectedOption(uploadedFiles[0].id);
        }

        await fetchNodeFileGroups(editor, resourceType);
    }, [editor, fetchNodeFileGroups, fetchNodeFileList, resourceType, setSelectedOption]);

    const renderFileSelectOption = useCallback((optionProps: React.HTMLAttributes<HTMLLIElement>, option: NodeFile) => (
        <FileSelectOption
            key={option.value}
            props={optionProps}
            option={option}
            onRemoveClick={handleOptionRemove}
            isAdmin={isAdmin}
        />
    ), [handleOptionRemove, isAdmin]);

    return (
        <div>
            <ContainerBox>
                <AutocompleteContainer onWheel={(e) => e.stopPropagation()}>
                    <Autocomplete
                        size="small"
                        fullWidth={true}
                        disablePortal={true}
                        disableClearable
                        value={selectedOption}
                        options={sortedOptions}
                        groupBy={(option) => option.group}
                        getOptionLabel={(option) => option.label}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        renderInput={(params) => <AutocompleteInput params={params} label={labelText}/>}
                        renderGroup={(params) => <CollapseGroup {...params}/>}
                        renderOption={renderFileSelectOption}
                        ListboxProps={listboxProps}
                        // HACK: there is an issue with autocomplete default popper in zoomed content
                        // (which clearly Rete.NodeEditor is)
                        // we 'fixed' it in CustomPopper component file - more info there
                        PopperComponent={CustomPopper}
                    />
                </AutocompleteContainer>
                <UploadButton
                    size="small"
                    onClick={handleUploadClick}>
                    <UploadFileIcon />
                </UploadButton>
            </ContainerBox>

            {showUploadPopup &&
                <UploadFileDialog
                    editor={editor}
                    onClose={handleUploadPopupClose}
                    onFilesUploaded={handleFilesUploaded}
                    groups={groups}
                    resourceType={resourceType}
                />
            }

            {showRemoveDialog && optionToRemove && createPortal(
                <RemoveFileDialog
                    onDelete={handleDialogDelete}
                    onClose={handleDialogClose}
                    fileName={optionToRemove.label}
                    usedBy={usedBy}
                />,
                document.body
            )}
        </div>
    )
};

export default memo(NodeFileSearch)